import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { ImagePlus, Loader2 } from "lucide-react";
import { useHospitalProfileStore } from "@/lib/stores/hospitalProfile";
import { Switch } from "@/components/ui/switch";
import { WallpaperUpload } from "./WallpaperUpload";

export function ProfileSettings() {
  const { toast } = useToast();
  const { profile, isLoading, error, fetchProfile, updateProfile } = useHospitalProfileStore();
  const [logoPreview, setLogoPreview] = useState<string>(profile.logoUrl);
  const [isSaving, setIsSaving] = useState(false);
  
  // Local form state
  const [formData, setFormData] = useState({
    name: profile.name,
    phone1: profile.phone1,
    phone2: profile.phone2,
    phone3: profile.phone3,
    email: profile.email,
    address: profile.address,
    website: profile.website,
    description: profile.description,
    usePrescriptionHeader: profile.usePrescriptionHeader ?? true
  });

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Update form data when profile changes
  useEffect(() => {
    setFormData({
      name: profile.name,
      phone1: profile.phone1,
      phone2: profile.phone2,
      phone3: profile.phone3,
      email: profile.email,
      address: profile.address,
      website: profile.website,
      description: profile.description,
      usePrescriptionHeader: profile.usePrescriptionHeader ?? true
    });
    setLogoPreview(profile.logoUrl);
  }, [profile]);

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setLogoPreview(result);
        // Don't auto-save logo, wait for save button
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value } as any));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        logoUrl: logoPreview,
        name: formData.name,
        phone1: formData.phone1,
        phone2: formData.phone2,
        phone3: formData.phone3,
        email: formData.email,
        address: formData.address,
        website: formData.website,
        description: formData.description,
        usePrescriptionHeader: formData.usePrescriptionHeader as boolean
      });
      
      toast({
        title: "Profile updated",
        description: "Your hospital profile has been saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading profile...
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-red-600">
            <p>Error loading profile: {error}</p>
            <Button onClick={fetchProfile} className="mt-4">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hospital Profile Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center space-x-4">
          <Avatar className="h-24 w-24">
            <AvatarImage src={logoPreview} alt="Hospital Logo" />
            <AvatarFallback>HQ</AvatarFallback>
          </Avatar>
          <div className="flex flex-col space-y-2">
            <div className="relative">
              <Input
                type="file"
                accept="image/*"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={handleLogoChange}
              />
              <Button type="button" variant="outline" className="w-full">
                <ImagePlus className="mr-2 h-4 w-4" />
                Upload Logo
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Recommended size: 200x200px
            </p>
          </div>
        </div>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Hospital Name</Label>
            <Input 
              id="name" 
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="phone1">Primary Phone</Label>
              <Input 
                id="phone1" 
                type="tel" 
                value={formData.phone1}
                onChange={(e) => handleInputChange('phone1', e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone2">Secondary Phone</Label>
              <Input 
                id="phone2" 
                type="tel" 
                value={formData.phone2}
                onChange={(e) => handleInputChange('phone2', e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone3">Emergency Phone</Label>
              <Input 
                id="phone3" 
                type="tel" 
                value={formData.phone3}
                onChange={(e) => handleInputChange('phone3', e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              type="email" 
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="address">Address</Label>
            <Textarea 
              id="address" 
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="website">Website</Label>
            <Input 
              id="website" 
              type="url" 
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea 
              id="description" 
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="min-h-[120px]"
            />
          </div>

          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <Label>Prescription Header & Footer</Label>
              <div className="text-xs text-muted-foreground">Enable to print with header and footer. Disable when using pre-printed letterhead.</div>
            </div>
            <Switch
              checked={!!formData.usePrescriptionHeader}
              onCheckedChange={(v) => handleInputChange('usePrescriptionHeader', v)}
            />
          </div>
        </div>

        {/* Wallpaper Upload Section */}
        <WallpaperUpload />

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
} 